import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, DatePicker, Form, Row, Select, notification } from 'antd';
import HTMLReactParser from 'html-react-parser';
import moment from 'moment';

import './CommissionExportModal.scss';
import AccountData from '../../../models/AccountData';
import { AccountFilter, AccountUsersAdministrationFilter } from '../../../models/AccountsFilter';
import { GenericModal } from '../../common/Modal/GenericModal';
import { useAppConfig } from '../../../Contexts/AppConfigContext';
import { exportAccountsCommissions, exportAccountUsersCommissions } from '../../../api/repositoryEP';
import { DownloadFileResult, saveBlobAsFile } from '../../../api/downloadFile';
import RestApiClient from '../../../api/restApiClient';
import { GetLanguageInUrl, IsLaguagePresentInUrl } from '../../../utils/urls';

const EXPORT_DATE_FORMAT: string = "YYYY-MM-DD";

const restApiClient: RestApiClient = new RestApiClient();

interface CommissionExportModalProps {
    visible: boolean;
    onCancel: () => void;
    filter?: AccountFilter;
    accountData?: AccountData;
    userFilter?: AccountUsersAdministrationFilter;
}

const slug = (value: string): string => value
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const CommissionExportModal = (props: CommissionExportModalProps): JSX.Element => {
    const { filter, accountData, userFilter } = props;

    const { t } = useTranslation();
    const appConfig = useAppConfig();
    const [form] = Form.useForm();

    const [exporting, setExporting] = useState<boolean>(false);

    const period = Form.useWatch('period', form);

    const statusTranslated = (status: string) => {
        switch (status) {
            case 'active':
                return t("components.accounts.filter.status-active");
            case 'inactive':
                return t("components.accounts.filter.status-inactive");
            case 'validation_needed':
                return t("components.accounts.filter.status-validation-needed");
            case 'confirm_needed':
                return t("components.accounts.filter.status-confirm-needed");
            default:
                return status;
        }
    };

    // filtros del buscador que se aplican al exportar
    const filters = accountData
        ? [
            { label: t("components.account-users.filter.name-and-surname"), value: userFilter?.name },
            { label: t("components.account-users.filter.email"), value: userFilter?.email },
            { label: t("components.account-users.filter.status"), value: userFilter?.status && statusTranslated(userFilter.status) },
        ]
        : [
            { label: t("components.accounts.filter.name"), value: filter?.name },
            { label: t("components.accounts.filter.email"), value: filter?.email },
            { label: t("components.accounts.filter.status"), value: filter?.status && statusTranslated(filter.status) },
            { label: t("components.accounts.filter.location"), value: filter?.location },
            { label: t("components.accounts.filter.account-type"), value: filter?.accountType && t(`components.accounts.list.account-type-${filter.accountType}`) },
        ];

    const activeFilters = filters.filter(x => x.value && x.value.trim() !== '');

    const getDescription = () => {
        if (accountData) {
            return activeFilters.length > 0
                ? t("components.accounts.commission-export.description-account-filtered", { 0: accountData.commercialName })
                : t("components.accounts.commission-export.description-account", { 0: accountData.commercialName });
        }

        return activeFilters.length > 0
            ? t("components.accounts.commission-export.description-filtered")
            : t("components.accounts.commission-export.description-all");
    };

    const getDates = (values: any): { dateFrom: string, dateTo: string } => {
        const today = moment();

        switch (values.period) {
            case 'week':
                return { dateFrom: today.clone().subtract(1, 'week').format(EXPORT_DATE_FORMAT), dateTo: today.format(EXPORT_DATE_FORMAT) };
            case 'custom':
                return { dateFrom: values.dateFrom.format(EXPORT_DATE_FORMAT), dateTo: values.dateTo.format(EXPORT_DATE_FORMAT) };
            default:
                return { dateFrom: today.clone().subtract(1, 'month').format(EXPORT_DATE_FORMAT), dateTo: today.format(EXPORT_DATE_FORMAT) };
        }
    };

    const getFileName = (dateFrom: string, dateTo: string): string => {
        const name = slug(t("components.accounts.commission-export.file-name") || '') || 'comisiones';
        const account = accountData ? `_${slug(accountData.commercialName ?? '') || 'cuenta'}` : '';

        return `${name}${account}_${dateFrom}_${dateTo}.xlsx`;
    };

    const isFutureDate = (current: any): boolean => current && current.valueOf() > moment().endOf('day').valueOf();

    const handleUnauthorizedExport = (status: number): void => {
        restApiClient.closeSession();

        if (status === 418) {
            //Logout, la sesión ha expirado
            window.sessionStorage.setItem("session-expired", "true");
            window.location.href = `${(IsLaguagePresentInUrl() ? GetLanguageInUrl() : '')}`;
        } else {
            //Logout, no está autorizado a realizar la operación que ha intentado realizar
            window.sessionStorage.setItem("no-authorized", "true");
            window.location.href = "/forbiden";
        }
    };

    const onFinish = async (values: any) => {
        const { dateFrom, dateTo } = getDates(values);

        setExporting(true);

        try {
            let response: DownloadFileResult | null = null;

            if (accountData && userFilter) {
                response = await exportAccountUsersCommissions(userFilter, dateFrom, dateTo);
            } else if (filter) {
                response = await exportAccountsCommissions(filter, dateFrom, dateTo);
            }

            if (response?.isFile && response.blob) {
                // El nombre lo pone el front: el navegador no puede leer Content-Disposition.
                saveBlobAsFile(response.blob, getFileName(dateFrom, dateTo));
                props.onCancel();
            } else if (response && (response.status === 401 || response.status === 418)) {
                handleUnauthorizedExport(response.status);
            } else {
                const message = response?.json?.message;

                notification.error({
                    message: t("components.accounts.commission-export.error")!,
                    description: (message && message.length > 0)
                        ? message
                        : t("components.accounts.commission-export.error-description")!,
                });
            }
        } catch (error) {
            console.error('Error exporting commissions:', error);

            notification.error({
                message: t("components.accounts.commission-export.error")!,
                description: t("components.accounts.commission-export.error-description")!,
            });
        } finally {
            setExporting(false);
        }
    };

    return (
        <GenericModal
            title={accountData ? t("components.accounts.commission-export.title-agents") : t("components.accounts.commission-export.title-accounts")}
            closable
            visible={props.visible}
            isLoading={exporting}
            onOk={() => form.submit()}
            onCancel={props.onCancel}
            content={
                <div className="commission-export-modal">
                    <p>{HTMLReactParser(getDescription())}</p>
                    {activeFilters.length > 0 && (
                        <ul className="export-filters">
                            {activeFilters.map(x => (
                                <li key={x.label}><span>{x.label}:</span> <strong>{x.value}</strong></li>
                            ))}
                        </ul>
                    )}
                    <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ period: 'month' }}>
                        <Form.Item name="period" label={t("components.accounts.commission-export.period")}>
                            <Select className="app-select">
                                <Select.Option value="week">{t("components.accounts.commission-export.last-week")}</Select.Option>
                                <Select.Option value="month">{t("components.accounts.commission-export.last-month")}</Select.Option>
                                <Select.Option value="custom">{t("components.accounts.commission-export.custom")}</Select.Option>
                            </Select>
                        </Form.Item>
                        {period === 'custom' && (
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="dateFrom"
                                        label={t("components.accounts.commission-export.date-from")}
                                        rules={[{ required: true, message: t("forms.validations.required-field")! }]}
                                    >
                                        <DatePicker className="app-input" format={appConfig.config.dateFormat} placeholder={t("components.bookings.filter.date-picker") || ''} disabledDate={isFutureDate} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="dateTo"
                                        label={t("components.accounts.commission-export.date-to")}
                                        dependencies={['dateFrom']}
                                        rules={[
                                            { required: true, message: t("forms.validations.required-field")! },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const dateFrom = getFieldValue('dateFrom');
                                                    if (dateFrom && value && value.isBefore(dateFrom, 'day')) {
                                                        return Promise.reject(t("components.accounts.commission-export.date-to-error"));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }),
                                        ]}
                                    >
                                        <DatePicker className="app-input" format={appConfig.config.dateFormat} placeholder={t("components.bookings.filter.date-picker") || ''} disabledDate={isFutureDate} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Form>
                    <small>{t("components.accounts.commission-export.info")}</small>
                </div>
            }
        />
    );
};

export default CommissionExportModal;
